import React, { useCallback, useEffect, useState } from "react";
import BankVerification from "./BankVerification";
import { BankDetails } from "../../interfaces/bank";
import IdentityVerification from "./IdentityVerification";
import BankingService from "../../api/bank-service";
import EstateVerificationService from "../../api/estate-verification-service";

type BankVerificationStatus = "not-started" | "pending" | "successful" | "failed";
type IdentityVerificationStatus = "not-started" | "pending" | "verified";
type ActiveScreen = "main" | "bank-verification" | "identity-verification";

const isCompleteStatus = (status?: string) => ["verified", "approved", "complete", "completed", "successful"].includes((status ?? "").toLowerCase());

const BankAccountVerification: React.FC<{ estateId: string }> = ({ estateId }) => {
  const [autoAccess, setAutoAccess] = useState(false);
  const [activeScreen, setActiveScreen] = useState<ActiveScreen>("main");
  const [bankVerificationStatus, setBankVerificationStatus] = useState<BankVerificationStatus>("not-started");
  const [identityStatus, setIdentityStatus] = useState<IdentityVerificationStatus>("not-started");
  const [savedBankDetails, setSavedBankDetails] = useState<BankDetails | null>(null);

  useEffect(() => {
    let mounted = true;
    if (!estateId) return;
    BankingService.getEstatePayoutAccount().then((record: any) => {
      if (!mounted || !record?.accountNumber) return;
      setSavedBankDetails({
        bankName: record.bankName ?? '', bankCode: record.bankCode ?? '',
        accountNumber: record.accountNumber, accountName: record.accountName ?? '', identityType: 'KYB',
      });
      const status = (record.status ?? record.verificationStatus ?? '').toLowerCase();
      setBankVerificationStatus(status === 'failed' || status === 'rejected' ? 'failed' : status === 'pending' || status === 'processing' ? 'pending' : 'successful');
    }).catch(() => {}).finally(() => { if (!mounted) return; });
    return () => { mounted = false; };
  }, [estateId]);

  const handleIdentitySave = useCallback((type: "KYC" | "KYB", status?: string) => {
    setIdentityStatus(isCompleteStatus(status) ? "verified" : "pending");
    setActiveScreen("main");
  }, []);

  const handleIdentityStatusChange = useCallback((status?: string) => {
    setIdentityStatus(isCompleteStatus(status) ? "verified" : "pending");
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!estateId) return;
    EstateVerificationService.getVerification(estateId).then((response: any) => {
      if (!mounted) return;
      const record = response?.data?.data ?? response?.data ?? response;
      if (record) setIdentityStatus(isCompleteStatus(record.status) ? "verified" : "pending");
    }).catch(() => {});
    return () => { mounted = false; };
  }, [estateId]);

  const isSectionDisabled = bankVerificationStatus !== "successful";

  const bankStatusConfig: Record<BankVerificationStatus, { label: string; color: string }> = {
    "not-started": { label: "Not Started", color: "text-gray-600" },
    pending:       { label: "Pending",     color: "text-amber-500" },
    successful:    { label: "Verified",    color: "text-green-600" },
    failed:        { label: "Failed",      color: "text-red-500"   },
  };

  // ── Bank Verification Screen ───────────────────────────────────────────────
  if (activeScreen === "bank-verification") {
    return (
      <BankVerification
        onBack={() => setActiveScreen("main")}
        onSuccess={(details) => {
          setSavedBankDetails(details);
          setBankVerificationStatus("successful");
          setActiveScreen("main");
        }}
        onFailed={(details) => {
          setSavedBankDetails(details);
          setBankVerificationStatus("failed");
          setActiveScreen("main");
        }}
      />
    );
  }

  // ── Identity Verification Screen ───────────────────────────────────────────
  if (activeScreen === "identity-verification") {
    return (
      <IdentityVerification estateId={estateId} onBack={() => setActiveScreen("main")} onSave={handleIdentitySave} onStatusChange={handleIdentityStatusChange} />
    );
  }

  // ── Main Screen ────────────────────────────────────────────────────────────
  return (
    <div className="w-full">

      {/* ── Bank Account Verification ── */}
      <div className="flex flex-col md:flex-row md:justify-between py-6">
        <p className="font-bold text-base md:text-lg text-black">
          Bank Account Verification
        </p>
        <span className={`text-sm font-medium ${bankStatusConfig[bankVerificationStatus].color}`}>
          {bankStatusConfig[bankVerificationStatus].label}
        </span>
      </div>

      {/* Bank details card — shown after any verification attempt */}
      {savedBankDetails ? (
        <div className="border border-gray-200 rounded-xl p-5 bg-gray-50 space-y-3">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Bank Name:</span>{"   "}{savedBankDetails.bankName}
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Account Name:</span>{"   "}{savedBankDetails.accountName}
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Account Number:</span>{"   "}{"*".repeat(6) + savedBankDetails.accountNumber.slice(-4)}
          </p>

          {bankVerificationStatus === "failed" && (
            <div className="flex items-center gap-2 pt-1">
              <span className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
              <span className="text-sm text-gray-500">Account name does not match estate details</span>
            </div>
          )}

          <div className="flex justify-end pt-1">
            <button
              onClick={() => setActiveScreen("bank-verification")}
              className="bg-black text-white text-xs px-4 py-2 rounded hover:bg-gray-800 active:scale-95 transition-all duration-200"
            >
              {bankVerificationStatus === "successful" ? "View Bank Details" : "Edit Bank Details"}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex md:justify-end">
          <button
            onClick={() => {
              setBankVerificationStatus("pending"); // ✅ set pending on click
              setActiveScreen("bank-verification");
            }}
            className="bg-black text-white text-xs px-4 py-2 rounded hover:bg-gray-800 active:scale-95 transition-all duration-200"
          >
            Start Bank Account Verification
          </button>
        </div>
      )}

      {/* ── Identity (KYC/KYB) Verification ── */}
      <div className="flex flex-col md:flex-row md:justify-between mt-10">
        <p className={`font-bold text-base md:text-lg ${isSectionDisabled ? "text-gray-300" : "text-black"}`}>
          Identity (KYC/KYB) Verification
        </p>
        {!isSectionDisabled && <span className={`text-sm font-medium ${identityStatus === 'verified' ? 'text-green-600' : identityStatus === 'pending' ? 'text-amber-500' : 'text-gray-500'}`}>
          {identityStatus === 'verified' ? 'Verified' : identityStatus === 'pending' ? 'In Progress' : 'Not Started'}
        </span>}
      </div>

      <div className="flex md:justify-end mt-4">
        <button
          disabled={isSectionDisabled}
          onClick={() => !isSectionDisabled && setActiveScreen("identity-verification")}
          className={`text-white text-xs px-4 py-2 rounded transition-all duration-200 ${
            isSectionDisabled
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-black hover:bg-gray-800 active:scale-95 cursor-pointer"
          }`}
        >
          {identityStatus === "verified" ? "View Identity Details" : identityStatus === "pending" ? "Resume Identity Verification" : "Start Identity Verification"}
        </button>
      </div>

      {/* ── Enable Automatic Access ── */}
      <div className="flex flex-col gap-2 md:flex-row md:justify-between md:items-center p-2.5 bg-gray-100 rounded-lg mt-10">
        <p className={`font-semibold text-sm ${isSectionDisabled ? "text-gray-300" : "text-gray-700"}`}>
          Enable automatic access
        </p>
        <button
          type="button"
          disabled={isSectionDisabled}
          onClick={() => !isSectionDisabled && setAutoAccess((prev) => !prev)}
          className={`relative w-10 h-6 rounded-full transition-colors duration-300 flex-shrink-0 ${
            autoAccess && !isSectionDisabled ? "bg-black" : "bg-gray-300"
          } ${isSectionDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
        >
          <span
            className={`absolute top-[2px] left-[2px] w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${
              autoAccess && !isSectionDisabled ? "translate-x-4" : ""
            }`}
          />
        </button>
      </div>

      <p className={`text-sm md:text-base font-medium mt-1 ${isSectionDisabled ? "text-gray-300" : "text-gray-500"}`}>
        if turned on, residents will automatically lose access if levy or verification
        conditions are not met.
      </p>

    </div>
  );
};

export default BankAccountVerification;
